import fetch from "auth/FetchInterceptor";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "constants/AuthConstant";
// Variáveis para controlar requisições em andamento e caching
let dashboardConfigRequestInProgress = false;
let configCache = null;
let configCacheTime = 0;
const CONFIG_CACHE_DURATION = 900000; // 15 minutos, já que os dados só são atualizados manualmente

const DashboardService = {};

DashboardService.getIndicadores = function () {
  return fetch({
    url: `${API_BASE_URL}/Indicadores`,
    method: "get",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

DashboardService.requestRefresh = function () {
  return fetch({
    url: `${API_BASE_URL}/Indicadores/refresh`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

// Obter configuração do dashboard do usuário autenticado
DashboardService.getDashboardConfig = async function (forceRefresh = false) {
  // Se forceRefresh for true, ignorar cache e buscar dados atualizados
  if (forceRefresh) {
    configCache = null;
    configCacheTime = 0;
  }

  // Verificar se o usuário está autenticado
  const token = localStorage.getItem(AUTH_TOKEN);
  if (!token) {
    console.error("[DashboardService] Token não encontrado");
    return Promise.reject(new Error("Usuário não autenticado"));
  }

  // Se já existe uma requisição em andamento, retornar uma promise que será resolvida quando a requisição original terminar
  if (dashboardConfigRequestInProgress) {
    return new Promise((resolve, reject) => {
      const checkCache = () => {
        if (configCache) {
          resolve({ status: 200, data: configCache });
        } else if (!dashboardConfigRequestInProgress) {
          // Se a requisição terminou mas não temos cache, tentar novamente
          setTimeout(() => {
            DashboardService.getDashboardConfig().then(resolve).catch(reject);
          }, 300);
        } else {
          // Continuar aguardando
          setTimeout(checkCache, 200);
        }
      };
      setTimeout(checkCache, 200);
    });
  }

  // Verificar se temos um cache válido
  const now = Date.now();
  if (
    !forceRefresh &&
    configCache &&
    now - configCacheTime < CONFIG_CACHE_DURATION
  ) {
    return { status: 200, data: configCache };
  }

  try {
    dashboardConfigRequestInProgress = true;

    const response = await fetch({
      url: `${API_BASE_URL}/DashboardConfig/meu-perfil`,
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200 && response.data) {
      // Atualizar cache
      configCache = response.data;
      configCacheTime = Date.now();

      // Salvar também no sessionStorage para recuperação em caso de problemas
      try {
        sessionStorage.setItem(
          "dashboard_config_cache",
          JSON.stringify(response.data)
        );
      } catch (e) {
        console.error(
          "[DashboardService] Erro ao salvar configuração no sessionStorage:",
          e
        );
      }
    }

    return response;
  } catch (error) {
    if (error.response?.status === 404) {
      // Retornar status especial para o frontend
      return { status: 404, firstLogin: true };
    }
    console.error(
      "[DashboardService] Erro ao buscar configuração do dashboard:",
      error
    );
    console.error("[DashboardService] Detalhes do erro:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });

    // Em caso de erro, tentar recuperar do sessionStorage
    try {
      const cachedConfig = sessionStorage.getItem("dashboard_config_cache");
      if (cachedConfig) {
        const parsedConfig = JSON.parse(cachedConfig);
        console.log(
          "[DashboardService] Usando configuração do cache após erro"
        );
        return { status: 200, data: parsedConfig };
      }
    } catch (e) {
      console.error(
        "[DashboardService] Erro ao recuperar configuração do sessionStorage:",
        e
      );
    }

    throw error;
  } finally {
    dashboardConfigRequestInProgress = false;
  }
};

// Obter configuração do dashboard de um usuário específico
DashboardService.getUserDashboardConfig = function (userId) {
  return fetch({
    url: `${API_BASE_URL}/DashboardConfig/usuario/${userId}`,
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
};

// Salvar configuração do dashboard
DashboardService.saveDashboardConfig = function (configData) {
  // Limpar cache ao salvar
  configCache = null;

  // Log detalhado para debug
  // console.log(
  //   "[DashboardService] Preparando para salvar configuração do dashboard:",
  //   {
  //     configData: JSON.stringify(configData, null, 2),
  //   }
  // );

  // Não precisamos mais obter o ID do usuário aqui, pois o backend vai usar o IUserContext
  // para pegar essa informação do token JWT

  // SIMPLIFICAÇÃO: Usar apenas um payload mínimo e seguro
  // Isso evita erros 400 Bad Request causados por dados complexos ou inválidos
  const configMinimalSafe = {};

  // Se houver um ID de configuração existente, adicionar ao objeto
  if (configData && configData.id && configData.id > 0) {
    configMinimalSafe.id = configData.id;
    // console.log(
    //   `[DashboardService] Usando ID existente da configuração: ${configData.id}`
    // );
  }

  // Layout: só incluir se for válido
  if (configData && configData.layout) {
    try {
      // Se for um objeto, converter para string
      let layoutStr =
        typeof configData.layout === "object"
          ? JSON.stringify(configData.layout)
          : configData.layout;

      // Validar que é um JSON válido
      JSON.parse(layoutStr);
      configMinimalSafe.layout = layoutStr;
      // console.log("[DashboardService] Layout verificado e válido");
    } catch (e) {
      // console.warn("[DashboardService] Layout inválido, usando array vazio");
      configMinimalSafe.layout = "[]";
    }
  } else {
    // console.log("[DashboardService] Nenhum layout fornecido");
    configMinimalSafe.layout = "[]";
  }

  // Cards: validar cada card e incluir apenas os válidos
  if (
    configData &&
    configData.cards &&
    Array.isArray(configData.cards) &&
    configData.cards.length > 0
  ) {
    const safeCards = [];

    configData.cards.forEach((card, index) => {
      // Verificar se o card tem o ID necessário
      if (!card.cardId) {
        // console.warn(`[DashboardService] Card ${index} sem ID, ignorando`);
        return;
      }

      const safeCard = {
        cardId: card.cardId,
        ordem: card.ordem || index,
        visivel: card.visivel === false ? false : true,
        configuracoes: card.configuracoes || null,
      };

      safeCards.push(safeCard);
    });

    if (safeCards.length > 0) {
      configMinimalSafe.cards = safeCards;
      // console.log(
      //   `[DashboardService] ${safeCards.length} cards verificados e válidos`
      // );
    } else {
      // console.warn("[DashboardService] Nenhum card válido para incluir");
    }
  } else {
    // console.log("[DashboardService] Nenhum card fornecido");
  }

  // console.log(
  //   "[DashboardService] Payload simplificado e seguro:",
  //   configMinimalSafe
  // );

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // console.log(
  //   "[DashboardService] Enviando requisição para: https://localhost:7240/DashboardConfig",
  //   {
  //     method: "POST",
  //     headers,
  //     body: JSON.stringify(configMinimalSafe, null, 2),
  //   }
  // );

  // Adicionar tratamento de erro para verificar respostas 400
  return fetch({
    url: `${API_BASE_URL}/DashboardConfig`,
    method: "post",
    headers,
    data: configMinimalSafe,
    validateStatus: function (status) {
      return status >= 200 && status < 300; // default
    },
  })
    .then((response) => {
      // console.log(
      //   "[DashboardService] Resposta do servidor ao salvar configuração:",
      //   {
      //     status: response.status,
      //     data: response.data,
      //   }
      // );
      return response;
    })
    .catch((error) => {
      console.error("[DashboardService] Erro na requisição:", error);
      if (error.response) {
        console.error("[DashboardService] Dados do erro:", {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data,
        });

        // Log detalhado do payload enviado para análise de erros críticos
        console.error("[DashboardService] Payload que causou erro:", {
          configData: JSON.stringify(configData, null, 2),
        });

        // Salvar no localStorage para análise posterior
        localStorage.setItem(
          "dashboard_last_error_payload",
          JSON.stringify(configMinimalSafe)
        );
        localStorage.setItem(
          "dashboard_last_error_time",
          new Date().toISOString()
        );
      }
      throw error;
    });
};

// Resetar configuração do dashboard para o padrão (usuário autenticado)
DashboardService.resetDashboardConfig = function () {
  // Limpar cache ao resetar
  configCache = null;

  // Depois de resetar, fazer uma atualização para garantir que a seção integracao_dinamica esteja incluída
  return fetch({
    url: `${API_BASE_URL}/DashboardConfig/reset`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }).then((response) => {
    if (response.status === 200) {
      // Garantir que a seção integracao_dinamica esteja incluída no layout
      const defaultLayout = [
        { id: "indicadores", title: "INDICADORES" },
        { id: "obras", title: "OBRAS EM ANDAMENTO" },
        { id: "receitas", title: "RECEITAS FUTURAS (PREVISTO)" },
        { id: "solicitacoes_atendimento", title: "SERVIÇOS EM ATENDIMENTO" },
        { id: "solicitacoes_fila", title: "SOLICITAÇÃO DE SERVIÇOS" },
        {
          id: "solicitacoes_finalizados",
          title: "TOTAL DE SERVIÇOS FINALIZADOS",
        },

        { id: "solicitacoes_agua", title: "SOLICITAÇÕES DE ÁGUA" },

        { id: "integrations", title: "INTEGRAÇÕES" },
        { id: "integracao_dinamica", title: "API" },
      ];

      // Atualizar o layout após o reset
      return DashboardService.atualizarLayout(JSON.stringify(defaultLayout))
        .then(() => {
          // Garantir que os cards da API estejam visíveis
          return DashboardService.getDashboardConfig(true).then(
            (configResponse) => {
              if (configResponse.status === 200 && configResponse.data) {
                const cards = configResponse.data.cards || [];
                const apiCards = cards.filter((card) =>
                  card.cardId.startsWith("integracao_dinamica-component-")
                );

                if (apiCards.length > 0) {
                  const updatedCards = apiCards.map((card) => ({
                    ...card,
                    visivel: true,
                  }));

                  return DashboardService.atualizarVisibilidadeCards(
                    updatedCards
                  )
                    .then(() => response)
                    .catch(() => response);
                }
              }
              return response;
            }
          );
        })
        .catch(() => response); // Mesmo se falhar a atualização, retornar a resposta original
    }
    return response;
  });
};

// Resetar configuração do dashboard para o padrão (usuário específico)
DashboardService.resetUserDashboardConfig = function (userId) {
  return fetch({
    url: `${API_BASE_URL}/DashboardConfig/usuario/${userId}/reset`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
};

// Atualizar apenas a visibilidade dos cards
DashboardService.atualizarVisibilidadeCards = function (cards) {
  // Limpar cache ao atualizar visibilidade
  configCache = null;
  configCacheTime = 0;

  // Limpar cache do sessionStorage também
  try {
    sessionStorage.removeItem("dashboard_config_cache");
    // console.log(
    //   "[DashboardService] Cache do sessionStorage limpo para forçar nova consulta após atualização"
    // );
  } catch (e) {
    console.error(
      "[DashboardService] Erro ao limpar cache do sessionStorage:",
      e
    );
  }

  if (!cards || !Array.isArray(cards) || cards.length === 0) {
    console.error(
      "[DashboardService] Lista de cards inválida para atualização de visibilidade"
    );
    return Promise.reject(new Error("Lista de cards inválida"));
  }

  // console.log("[DashboardService] Atualizando visibilidade de cards:", cards);

  // Não precisamos mais verificar informações do usuário, o backend cuidará disso

  // Formatar os dados de acordo com o novo formato esperado pelo backend
  const requestData = {
    cards: cards.map((card) => ({
      cardId: card.cardId,
      visivel: card.visivel,
      ordem: card.ordem || 0,
      configuracoes: card.configuracoes || "{}",
    })),
  };

  // Verificar se cada card tem um cardId e configuracoes válido
  requestData.cards.forEach((card) => {
    if (!card.cardId) {
      console.error(
        `[DashboardService] Card sem ID válido: ${JSON.stringify(card)}`
      );
    }

    if (!card.configuracoes) {
      card.configuracoes = "{}";
      // console.log(
      //   `[DashboardService] Definindo configurações padrão para o card ${card.cardId}`
      // );
    }
  });

  // console.log("[DashboardService] Dados formatados para envio:", requestData);

  // Definir um timeout maior para esta operação crítica
  const timeout = 30000; // 30 segundos

  return fetch({
    url: `${API_BASE_URL}/DashboardConfig/atualizar-visibilidade`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    data: requestData,
    timeout: timeout, // Definir timeout maior que o padrão
    validateStatus: function (status) {
      return status >= 200 && status < 300;
    },
  })
    .then((response) => {
      // console.log(
      //   "[DashboardService] Resposta da atualização de visibilidade:",
      //   {
      //     status: response.status,
      //     data: response.data,
      //   }
      // );

      // CRÍTICO: Verificar se a resposta contém dados válidos antes de atualizar o cache
      if (response.status === 200) {
        if (response.data) {
          // Atualizar o cache local com os novos dados
          configCache = response.data;
          configCacheTime = Date.now();

          // Atualizar também o sessionStorage
          try {
            sessionStorage.setItem(
              "dashboard_config_cache",
              JSON.stringify(response.data)
            );
            // console.log(
            //   "[DashboardService] Cache atualizado após alteração de visibilidade"
            // );
          } catch (e) {
            console.error(
              "[DashboardService] Erro ao atualizar cache no sessionStorage:",
              e
            );
          }

          // Registrar a operação como bem-sucedida
          localStorage.setItem("last_visibility_update", Date.now().toString());
          localStorage.setItem("last_visibility_update_status", "success");
        } else {
          // console.warn(
          //   "[DashboardService] Resposta sem dados, não atualizando cache"
          // );
          localStorage.setItem(
            "last_visibility_update_status",
            "warning:no_data"
          );
        }
      }

      return response;
    })
    .catch((error) => {
      console.error(
        "[DashboardService] Erro ao atualizar visibilidade:",
        error
      );

      // Registrar o erro para análise
      localStorage.setItem("last_visibility_update", Date.now().toString());
      localStorage.setItem("last_visibility_update_status", "error");
      localStorage.setItem(
        "last_visibility_update_error",
        JSON.stringify(error.message || "Erro desconhecido")
      );

      if (error.response) {
        console.error("[DashboardService] Detalhes do erro:", {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data,
        });

        // Tratar erros específicos
        if (error.response.status === 401) {
          console.error(
            "[DashboardService] Erro de autenticação ao atualizar visibilidade"
          );
        } else if (error.response.status === 400) {
          console.error(
            "[DashboardService] Erro de validação ao atualizar visibilidade"
          );
        } else if (error.response.status === 404) {
          console.error(
            "[DashboardService] Endpoint não encontrado ao atualizar visibilidade"
          );
        } else if (error.response.status >= 500) {
          console.error(
            "[DashboardService] Erro no servidor ao atualizar visibilidade"
          );
        }
      } else if (error.request) {
        // A requisição foi feita mas não houve resposta
        console.error(
          "[DashboardService] Sem resposta do servidor ao atualizar visibilidade"
        );
      }

      throw error;
    });
};

// Atualizar apenas o layout do dashboard
DashboardService.atualizarLayout = async function (layout) {
  // Limpar cache ao atualizar layout
  configCache = null;

  if (!layout) {
    console.error("[DashboardService] Layout inválido para atualização");
    return Promise.reject(new Error("Layout inválido"));
  }

  console.log("[DashboardService] Atualizando layout do dashboard");

  // Adicionar usuarioId ao payload
  const usuarioId = parseInt(localStorage.getItem("USER_ID"));
  const requestData = {
    layout: typeof layout === "string" ? layout : JSON.stringify(layout),
    usuarioId: usuarioId,
    ehPadrao: false,
  };

  try {
    const response = await fetch({
      url: `${API_BASE_URL}/DashboardConfig/atualizar-layout`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      data: requestData,
      validateStatus: function (status) {
        return status >= 200 && status < 300;
      },
    });

    return response;
  } catch (error) {
    console.error("[DashboardService] Erro ao atualizar layout:", error);
    // Se o erro for 404 (configuração não encontrada), retornar status especial para o frontend
    if (error.response?.status === 404) {
      return { status: 404, firstLogin: true };
    }
    throw error;
  }
};

// Método específico para restaurar uma seção inteira
DashboardService.restaurarSecao = function (sectionId) {
  // Limpar cache ao restaurar seção
  configCache = null;
  configCacheTime = 0;

  // Limpar cache do sessionStorage
  try {
    sessionStorage.removeItem("dashboard_config_cache");
    // console.log(
    //   "[DashboardService] Cache do sessionStorage limpo para forçar nova consulta após restauração de seção"
    // );
  } catch (e) {
    console.error(
      "[DashboardService] Erro ao limpar cache do sessionStorage:",
      e
    );
  }

  if (!sectionId) {
    console.error("[DashboardService] ID da seção inválido para restauração");
    return Promise.reject(new Error("ID da seção inválido"));
  }

  // console.log(`[DashboardService] Restaurando seção: ${sectionId}`);

  // Obter configuração atual do cache se disponível
  let cachedConfig = null;
  try {
    const cachedConfigStr = sessionStorage.getItem("dashboard_config_cache");
    if (cachedConfigStr) {
      cachedConfig = JSON.parse(cachedConfigStr);
      // console.log(
      //   "[DashboardService] Usando configuração em cache para identificar cards"
      // );
    }
  } catch (e) {
    console.error("[DashboardService] Erro ao ler cache:", e);
  }

  // Se temos configuração em cache com cards da seção, restaurar diretamente
  if (cachedConfig && cachedConfig.cards && Array.isArray(cachedConfig.cards)) {
    const sectionCards = cachedConfig.cards.filter(
      (card) => card.cardId && card.cardId.startsWith(`${sectionId}-component-`)
    );

    if (sectionCards.length > 0) {
      // console.log(
      //   `[DashboardService] Usando ${sectionCards.length} cards do cache para restaurar seção ${sectionId}`
      // );

      // Criar lista de cards para restauração
      const cardsToRestore = sectionCards.map((card) => ({
        cardId: card.cardId,
        visivel: true,
        ordem: card.ordem || 0,
        configuracoes: card.configuracoes || "{}",
      }));

      // Chamar diretamente o endpoint de atualização de visibilidade
      // console.log(
      //   `[DashboardService] Restaurando ${cardsToRestore.length} cards da seção ${sectionId} (direto do cache)`
      // );
      return DashboardService.atualizarVisibilidadeCards(cardsToRestore);
    }
  }

  // Se não temos cache ou não encontramos cards no cache, precisamos obter a configuração
  // console.log(
  //   "[DashboardService] Obtendo configuração atual para identificar cards da seção"
  // );

  return DashboardService.getDashboardConfig(true)
    .then((configResponse) => {
      if (configResponse.status !== 200 || !configResponse.data) {
        console.error(
          "[DashboardService] Erro ao obter configuração para restauração de seção"
        );
        throw new Error("Configuração não disponível");
      }

      const config = configResponse.data;

      // Identificar todos os cards da seção
      const cardsToRestore = [];

      if (config.cards && Array.isArray(config.cards)) {
        const sectionCards = config.cards.filter(
          (card) =>
            card.cardId && card.cardId.startsWith(`${sectionId}-component-`)
        );

        // console.log(
        //   `[DashboardService] Encontrados ${sectionCards.length} cards para a seção ${sectionId}`
        // );

        // Adicionar todos os cards à lista de restauração
        sectionCards.forEach((card) => {
          cardsToRestore.push({
            cardId: card.cardId,
            visivel: true, // Forçar visibilidade
            ordem: card.ordem || 0,
            configuracoes: card.configuracoes || "{}",
          });
        });
      }

      if (cardsToRestore.length === 0) {
        // console.warn(
        //   `[DashboardService] Nenhum card encontrado para a seção ${sectionId}`
        // );
        return {
          status: 204,
          message: "Nenhum card encontrado para restaurar",
        };
      }

      // console.log(
      //   `[DashboardService] Restaurando ${cardsToRestore.length} cards da seção ${sectionId}`
      // );

      // Chamar o endpoint de atualização de visibilidade com todos os cards da seção
      return DashboardService.atualizarVisibilidadeCards(cardsToRestore);
    })
    .catch((error) => {
      console.error(
        `[DashboardService] Erro ao restaurar seção ${sectionId}:`,
        error
      );
      throw error;
    });
};

// Criar configuração inicial do dashboard
DashboardService.criarConfiguracaoInicial = function () {
  console.log("[DashboardService] Criando configuração inicial do dashboard");

  const configInicial = {
    layout: JSON.stringify([
      { id: "receitas", title: "RECEITAS FUTURAS (PREVISTO)" },
      { id: "solicitacoes_atendimento", title: "SERVIÇOS EM ATENDIMENTO" },
      { id: "solicitacoes_fila", title: "SOLICITAÇÃO DE SERVIÇOS" },
      {
        id: "solicitacoes_finalizados",
        title: "TOTAL DE SERVIÇOS FINALIZADOS",
      },
      { id: "obras", title: "OBRAS EM ANDAMENTO" },
      { id: "solicitacoes_agua", title: "SOLICITAÇÕES DE ÁGUA" },
      { id: "indicadores", title: "INDICADORES" },
      { id: "integrations", title: "INTEGRAÇÕES" },
      { id: "integracao_dinamica", title: "API" },
    ]),
    cards: [],
  };

  return fetch({
    url: `${API_BASE_URL}/DashboardConfig`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    data: configInicial,
  });
};

export default DashboardService;
